import { useCallback } from 'react';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { IconButton } from '@mui/material';
import { GridColDef, GridRowId, GridRowSelectionModel, GridRowsProp } from '@mui/x-data-grid-pro';
import { useFormikContext } from 'formik';

import { PanelGrid } from 'components/grid';
import { PermissoesProps, UsuarioGrupoUsuario } from 'pages/cadastro/permissoes/types';

/**
 * Retorna a lista de usuários para ser exibida na grid.
 */
const getRows = (data: UsuarioGrupoUsuario[] = []): GridRowsProp =>
    data.map((item) => ({
        id: item.idUsuario,
        dsUsuario: item.dsUsuario,
    }));

type PermissoesUsuariosGridProps = {
    selectionModel: GridRowSelectionModel;
    setSelectionModel: (model: GridRowSelectionModel) => void;
};

export function PermissoesUsuariosGrid({ selectionModel, setSelectionModel }: PermissoesUsuariosGridProps) {
    const { values, setFieldValue } = useFormikContext<PermissoesProps>();

    /**
     * Remove um usuário específico da lista.
     */
    const handleRemoveUsuario = useCallback(
        (idUsuario: number) => {
            const usuarios = (values.usuarios || []).filter((u) => u.idUsuario !== idUsuario);
            setFieldValue('usuarios', usuarios);

            setSelectionModel({
                ...selectionModel,
                ids: new Set([...selectionModel.ids].filter((id) => id !== idUsuario)),
            });
        },
        [values.usuarios, setFieldValue, selectionModel, setSelectionModel],
    );

    const columns: GridColDef[] = [
        { field: 'id', headerName: 'Código', width: 120, minWidth: 70 },
        { field: 'dsUsuario', headerName: 'Nome Usuário', flex: 1, minWidth: 100 },
        {
            field: 'actions',
            headerName: '',
            width: 60,
            sortable: false,
            filterable: false,
            hideable: false,
            disableColumnMenu: true,
            renderCell: (params) => (
                <IconButton color="error" onClick={() => handleRemoveUsuario(params.row.id)} sx={{ p: 1 }}>
                    <DeleteOutlineIcon />
                </IconButton>
            ),
        },
    ];

    return (
        <PanelGrid
            checkboxSelection
            rows={getRows(values.usuarios)}
            reload={false}
            remote={false}
            loading={false}
            columns={columns}
            selectionModel={selectionModel}
            onClickRow={setSelectionModel}
            customMessageNoRows="Sem usuários adicionados. Para adicionar, clique em adicionar usuários."
        />
    );
}
